// Mining Admin Panel API Service
import { sessionExpired } from "../lib/sessionExpired";
// Base URL for Mining APIs
const MINING_API_BASE_URL = process.env.REACT_APP_MINING_API_URL;
// const MINING_API_BASE_URL = "https://minestage.ginox.io/api/admin";

// ==================== Token Management ====================

export const storeMiningAccessToken = (token) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("mining_access_token", token);
  }
};

export const getMiningAccessToken = () => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("mining_access_token");
  }
  return null;
};

export const removeMiningAccessToken = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("mining_access_token");
  }
};

// ==================== User Data Management ====================

export const storeMiningUserData = (user) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("mining_user_data", JSON.stringify(user));
  }
};

export const getMiningUserData = () => {
  if (typeof window !== "undefined") {
    const userData = localStorage.getItem("mining_user_data");
    return userData ? JSON.parse(userData) : null;
  }
  return null;
};

export const removeMiningUserData = () => {
  if (typeof window !== "undefined") {
    localStorage.removeItem("mining_user_data");
  }
};

// ==================== Helper Functions ====================

const getMiningAuthHeaders = () => {
  const token = getMiningAccessToken();
  return {
    "Content-Type": "application/json",
    ...(token && { token: token }),
  };
};

// Add response interceptor to handle token expiration
const handleApiResponse = async (response) => {
  if (response.status === 401) {
    sessionExpired();
    throw new Error("Authentication expired. Please login again.");
  }
  return response;
};

// ==================== API Functions ====================

// Login API
export const loginMiningAPI = async (payload) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: process.env.REACT_APP_MINING_API_KEY,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Login API error:", error);
    throw error;
  }
};

// Get Stats API
export const getMiningStatsAPI = async () => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/stats`, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Stats API error:", error);
    throw error;
  }
};

// Get Users API (Active Users)
export const getMiningUsersAPI = async (page = 1, pageSize = 10, search = "", userId = "") => {
  try {
    let url = `${MINING_API_BASE_URL}/get-users-list?page=${page}&pageSize=${pageSize}&order=desc`;
    
    // Add search parameter if provided
    if (search && search.trim() !== "") {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    
    // Add userId parameter if provided
    if (userId && userId.trim() !== "") {
      url += `&userId=${encodeURIComponent(userId.trim())}`;
    }
    
    const response = await fetch(url, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Users API error:", error);
    throw error;
  }
};

// Get Inactive Users API
export const getInactiveMiningUsersAPI = async (page = 1, pageSize = 10) => {
  try {
    const response = await fetch(
      `${MINING_API_BASE_URL}/get-users-list?page=${page}&pageSize=${pageSize}&order=desc&status=Inactive`,
      {
        method: "GET",
        headers: getMiningAuthHeaders(),
      }
    );

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Inactive Users API error:", error);
    throw error;
  }
};

// Get User Details API
export const getMiningUserDetailsAPI = async (userId) => {
  try {
    const response = await fetch(
      `${MINING_API_BASE_URL}/get-user-details?userId=${userId}`,
      {
        method: "GET",
        headers: getMiningAuthHeaders(),
      }
    );

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining User Details API error:", error);
    throw error;
  }
};

// Update User Status API
export const updateMiningUserStatusAPI = async (payload) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/update-user-status`, {
      method: "POST",
      headers: getMiningAuthHeaders(),
      body: JSON.stringify(payload),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Update User Status API error:", error);
    throw error;
  }
};

// Get Wallet Statement API
export const getMiningWalletStatementAPI = async (
  page = 1,
  pageSize = 10,
  wallet_type = "token",
  userId = ""
) => {
  try {
    let url = `${MINING_API_BASE_URL}/get-wallet-statement?page=${page}&pageSize=${pageSize}&order=desc&wallet_type=${wallet_type}`;
    
    // Add userId parameter if provided
    if (userId && userId.trim() !== "") {
      url += `&userId=${encodeURIComponent(userId.trim())}`;
    }
    
    const response = await fetch(url, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Wallet Statement API error:", error);
    throw error;
  }
};

// Get Income History API
export const getMiningIncomeHistoryAPI = async (
  page = 1,
  pageSize = 10,
  incomeType = "",
  userId = ""
) => {
  try {
    let url = `${MINING_API_BASE_URL}/get-income-history?page=${page}&pageSize=${pageSize}&order=desc`;
    
    if (incomeType) {
      url += `&incomeType=${incomeType}`;
    }
    
    if (userId && userId.trim() !== "") {
      url += `&userId=${encodeURIComponent(userId.trim())}`;
    }
    
    const response = await fetch(url, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Mining Income History API error:", error);
    throw error;
  }
};

// Get Socials List API
export const getSocialsListAPI = async (status = null) => {
  try {
    let url = `${MINING_API_BASE_URL}/get-socials-list`;

    // Add status parameter when status=true or status=false is requested
    if (status === true) {
      url += `?status=true`;
    } else if (status === false) {
      url += `?status=false`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Get Socials List API error:", error);
    throw error;
  }
};

// Approve Social API
export const approveSocialAPI = async (socialId) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/approve-social`, {
      method: "POST",
      headers: getMiningAuthHeaders(),
      body: JSON.stringify({ id: socialId }),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Approve Social API error:", error);
    throw error;
  }
};

// ==================== Withdraw Request APIs ====================

// Get Withdrawables API - Get list of users with withdrawable amounts
export const getWithdrawablesAPI = async () => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/withdrawables`, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Get Withdrawables API error:", error);
    throw error;
  }
};

// Withdraw API - Process withdrawal for selected users
export const withdrawAPI = async (userIds) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/withdraw`, {
      method: "POST",
      headers: getMiningAuthHeaders(),
      body: JSON.stringify({ userIds }),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Withdraw API error:", error);
    throw error;
  }
};

// Get Withdraw History API - Get paginated withdraw history with filters
export const getWithdrawHistoryAPI = async (
  page = 1,
  pageSize = 10,
  order = "desc",
  status = "",
  startDate = "",
  endDate = "",
  userId = ""
) => {
  try {
    let url = `${MINING_API_BASE_URL}/withdraw/history?page=${page}&pageSize=${pageSize}&order=${order}`;

    if (status) {
      url += `&status=${status}`;
    }

    if (startDate) {
      url += `&startDate=${startDate}`;
    }

    if (endDate) {
      url += `&endDate=${endDate}`;
    }

    if (userId && userId.trim() !== "") {
      url += `&userId=${encodeURIComponent(userId.trim())}`;
    }

    const response = await fetch(url, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Get Withdraw History API error:", error);
    throw error;
  }
};

// ==================== Energy Cards APIs ====================

// Get Energy Cards API - Get list of all energy cards
export const getEnergyCardsAPI = async () => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/cards/energy-cards`, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Get Energy Cards API error:", error);
    throw error;
  }
};

// Add Energy Card API - Create a new energy card
export const addEnergyCardAPI = async (payload) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/cards/energy-cards/add-new`, {
      method: "POST",
      headers: getMiningAuthHeaders(),
      body: JSON.stringify(payload),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Add Energy Card API error:", error);
    throw error;
  }
};

// Update Energy Card API - Update an existing energy card
export const updateEnergyCardAPI = async (cardId, payload) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/cards/energy-cards/edit/${cardId}`, {
      method: "POST",
      headers: getMiningAuthHeaders(),
      body: JSON.stringify(payload),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Update Energy Card API error:", error);
    throw error;
  }
};

// Get Leaderboard Stats API
export const getLeaderboardStatsAPI = async () => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/leaderboard-stats`, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Leaderboard Stats API error:", error);
    throw error;
  }
};

// Get User Mined Tokens API
export const getMinedTokensAPI = async (page = 1, pageSize = 15) => {
  try {
    const response = await fetch(
      `${MINING_API_BASE_URL}/get-user-mined-tokens?page=${page}&pageSize=${pageSize}`,
      {
        method: "GET",
        headers: getMiningAuthHeaders(),
      }
    );

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Mined Tokens API error:", error);
    throw error;
  }
};

// Get Wallets API - type: token | energy | bnb
export const getWalletsAPI = async (
  type = "token",
  page = 1,
  pageSize = 15,
  order = "desc",
  orderBy = "amount"
) => {
  try {
    const response = await fetch(
      `${MINING_API_BASE_URL}/wallets/${type}?page=${page}&pageSize=${pageSize}&order=${order}&orderBy=${orderBy}`,
      {
        method: "GET",
        headers: getMiningAuthHeaders(),
      }
    );

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Wallets API error:", error);
    throw error;
  }
};

// Get Active Miners API
export const getActiveMinersAPI = async (page = 1, pageSize = 15) => {
  try {
    const response = await fetch(`${MINING_API_BASE_URL}/active-miners?page=${page}&pageSize=${pageSize}`, {
      method: "GET",
      headers: getMiningAuthHeaders(),
    });

    await handleApiResponse(response);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Active Miners API error:", error);
    throw error;
  }
};
